<template>
    <div>
        <!-- My goals, then one tab per person who's shared goals with you (their manager, teammates for
             team goals, Team Hub Admins for everyone's) -->
        <div v-if="loaded && (myEmployeeID || people.length)" class="sb-teams" role="tablist">
            <button v-if="myEmployeeID" type="button" role="tab" :aria-selected="mine" class="sb-team" :class="mine ? 'on' : ''" @click="selectPerson(null)">
                <span class="sb-team-name"><svg-icon icon="user" class="sb-team-ic" />My goals</span>
                <span class="sb-team-meta">{{ myGoals.length }} goal{{ myGoals.length == 1 ? '' : 's' }}</span>
            </button>
            <button v-for="p in people" :key="p.ID" type="button" role="tab" :aria-selected="!mine && p.ID == who" class="sb-team" :class="!mine && p.ID == who ? 'on' : ''" @click="selectPerson(p.ID)">
                <span class="sb-team-name">{{ p.Name }}</span>
                <span class="sb-team-meta">{{ p.Goals.length }} shared<template v-if="p.Behind"> · <b class="bad">{{ p.Behind }} behind</b></template></span>
            </button>
        </div>

        <template v-if="loaded">
            <div v-if="!who" class="pg-empty">
                <svg-icon icon="star" />
                <div>Personal goals need a Team Hub employee record. Contact your administrator to be linked to yours.</div>
            </div>

            <template v-else>
                <div class="pg-sum">
                    <div class="pg-sum-main">
                        <div class="pg-sum-k">{{ mine ? 'Your personal goals' : whoName + '\'s shared goals' }}</div>
                        <div class="pg-sum-v">{{ list.length }} goal{{ list.length == 1 ? '' : 's' }}</div>
                    </div>
                    <div class="pg-stat ok"><b>{{ count('on') }}</b><span>on track</span></div>
                    <div class="pg-stat bad"><b>{{ count('off') }}</b><span>behind</span></div>
                    <div class="pg-stat"><b>{{ count('done') }}</b><span>done</span></div>
                    <div class="pg-sum-msg">{{ mine ? 'Goals for you, not just your seat: work, skills, family, health, money. You choose who sees each one.' : 'You only see the goals they chose to share with you.' }}</div>
                    <div v-if="mine" class="btn pg-btn" @click="openGoal(null)">Add Goal</div>
                </div>

                <div v-if="list.length" class="pg-grid">
                    <!-- Your own cards open the goal (as rocks do); the buttons on them don't -->
                    <div v-for="g in list" :key="g.ID" class="pg-card" :class="[ statusInfo(g.Status).cls, mine ? 'pg-card-open' : '' ]" :role="mine ? 'button' : undefined" :tabindex="mine ? 0 : undefined" :aria-label="mine ? g.Name : undefined" @click="mine && openGoal(g)" @keydown.enter.self="mine && openGoal(g)">
                        <div class="pg-top">
                            <span class="pg-cat">{{ categoryLabel(g.Category) }}</span>
                            <span class="pg-st" :class="statusInfo(g.Status).cls">{{ statusInfo(g.Status).label }}</span>
                        </div>
                        <div class="pg-t">{{ g.Name }}</div>
                        <div v-if="g.Why" class="pg-why">{{ g.Why }}</div>
                        <div class="pg-prog">
                            <div class="pg-bar" :class="g.Status != 'off' ? 'ok' : ''"><span :style="'width:' + g.Percent + '%'"></span></div>
                            <span v-if="g.TrackType == 'number'"><b>{{ formatNumber(g.CurrentValue) }}</b> of {{ formatNumber(g.TargetValue) }} {{ g.Unit }}</span>
                            <span v-else-if="g.TrackType == 'steps'"><b>{{ g.Steps.filter(s => s.Done).length }}</b> of {{ g.Steps.length }} steps</span>
                            <span v-else>{{ g.Done ? 'Done' : 'Not yet' }}</span>
                        </div>
                        <div v-if="g.TrackType == 'steps' && g.Steps.length" class="pg-steps">
                            <span v-for="(s, i) in g.Steps" :key="i" :class="s.Done ? 'd' : ''"><i></i>{{ s.Text }}</span>
                        </div>
                        <div class="pg-meta">
                            <span><svg-icon icon="calendar" />{{ dueText(g) }}</span>
                            <span title="Who can see this">{{ visibilityLabel(g.Visibility) }}</span>
                        </div>
                        <div v-if="g.CheckIns.length" class="pg-last"><b>Last check-in {{ dateLabel(checkInDate(g.CheckIns[0])) }}:</b> {{ g.CheckIns[0].Note }}</div>
                        <div v-if="mine" class="pg-acts">
                            <div class="btn pg-btn" @click.stop="checkInGoal = g">Check In</div>
                            <div class="btn-light pg-btn" title="Add it to a meeting's agenda" @click.stop="bringToMeeting(g)">Bring to Meeting</div>
                            <span v-if="stale(g)" class="pg-stale"><svg-icon icon="clock" />{{ g.CheckIns.length ? 'No check-in in a month' : 'No check-in yet' }}</span>
                        </div>
                    </div>
                    <button v-if="mine" type="button" class="pg-add" @click="openGoal(null)">+ Add a Goal</button>
                </div>
                <div v-else class="pg-empty">
                    <svg-icon icon="star" />
                    <div>{{ mine ? 'No personal goals yet. Start with one that matters to you.' : 'Nothing shared yet.' }}</div>
                    <div v-if="mine" class="btn pg-btn" @click="openGoal(null)">Add Your First Goal</div>
                </div>
            </template>
        </template>

        <personal-goal-modal v-if="showGoal" :goal="goalToEdit" @close="showGoal = false" @saved="onSaved" />
        <personal-goal-check-in-modal v-if="checkInGoal" :goal="checkInGoal" @close="checkInGoal = null" @saved="onSaved" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import PersonalGoalModal from './PersonalGoalModal.vue';
import PersonalGoalCheckInModal from './PersonalGoalCheckInModal.vue';
import SvgIcon from './SvgIcon.vue';
import { categoryLabel, statusInfo, VISIBILITIES, isoDate, daysBetween, dateLabel, formatNumber } from '@/personalGoals';

export default defineComponent({
    name: 'PersonalGoalsView',
    components: {
        PersonalGoalModal,
        PersonalGoalCheckInModal,
        SvgIcon
    },
    data() {
        return {
            loaded: false,
            goals: [] as any[],
            myEmployeeID: null as any,
            selectedPerson: null as any, // ?person=<employee ID>; null is My goals
            showGoal: false,
            goalToEdit: null as any,
            checkInGoal: null as any,
            todayDate: isoDate(new Date())
        }
    },
    async created() {
        this.selectedPerson = this.$route.query.person ? Number(this.$route.query.person) : null;
        await this.load();
    },
    computed: {
        myGoals(): any[] {
            return this.myEmployeeID ? this.goals.filter((g: any) => g.OwnerEmployeeID == this.myEmployeeID) : [];
        },
        // Everyone else whose goals you can see (the API only sends those), in name order
        people(): any[] {
            const out: any[] = [];
            for(const g of this.goals) {
                if(g.OwnerEmployeeID == this.myEmployeeID) {
                    continue;
                }
                let p = out.find((x: any) => x.ID == g.OwnerEmployeeID);
                if(!p) {
                    p = { ID: g.OwnerEmployeeID, Name: g.OwnerName, Goals: [], Behind: 0 };
                    out.push(p);
                }
                p.Goals.push(g);
                if(g.Status == 'off') {
                    p.Behind++;
                }
            }
            return out.sort((a: any, b: any) => a.Name.localeCompare(b.Name));
        },
        // The person being looked at: the one picked, else you, else (no employee record) the first person
        who(): any {
            if(this.selectedPerson && this.people.some((p: any) => p.ID == this.selectedPerson)) {
                return this.selectedPerson;
            }
            return this.myEmployeeID || this.people[0]?.ID || null;
        },
        mine(): boolean {
            return !!this.myEmployeeID && this.who == this.myEmployeeID;
        },
        whoName(): string {
            return this.people.find((p: any) => p.ID == this.who)?.Name || '';
        },
        list(): any[] {
            return this.goals.filter((g: any) => g.OwnerEmployeeID == this.who);
        }
    },
    methods: {
        categoryLabel,
        statusInfo,
        dateLabel,
        formatNumber,
        async load() {
            await axios.get(`${this.$store.state.apiUrl}/api/personalgoals`)
                .then(resp => {
                    this.goals = resp.data.Goals;
                    this.myEmployeeID = resp.data.MyEmployeeID;
                })
                .catch(e => {
                    console.log(e);
                })
            this.loaded = true;
        },
        selectPerson(id: any) {
            this.selectedPerson = id;
            this.$router.replace({ query: { ...this.$route.query, person: id || undefined } });
        },
        count(status: string): number {
            return this.list.filter((g: any) => g.Status == status).length;
        },
        visibilityLabel(value: string): string {
            return VISIBILITIES.find(v => v.value == value)?.label || '';
        },
        checkInDate(checkIn: any): string {
            return isoDate(new Date(checkIn.Date));
        },
        dueText(g: any): string {
            if(!g.DueDate) {
                return 'No due date';
            }
            const days = daysBetween(this.todayDate, g.DueDate);
            if(g.Status == 'done') {
                return 'Due ' + dateLabel(g.DueDate);
            }
            if(days < 0) {
                return `${-days} day${days == -1 ? '' : 's'} past due`;
            }
            return days == 0 ? 'Due today' : `${dateLabel(g.DueDate)} · ${days} day${days == 1 ? '' : 's'} left`;
        },
        // Not done, and no check-in in the last 30 days
        stale(g: any): boolean {
            if(g.Status == 'done') {
                return false;
            }
            return !g.CheckIns.length || daysBetween(this.checkInDate(g.CheckIns[0]), this.todayDate) > 30;
        },
        openGoal(g: any) {
            this.goalToEdit = g;
            this.showGoal = true;
        },
        // The header's Agenda Item quick add, filled in with the goal, so it can go on any meeting
        bringToMeeting(g: any) {
            this.$store.state.quickAddPreset = { Name: `Personal goal: ${g.Name} (${g.Percent}%${g.Status == 'off' ? ', behind' : ''})` };
            this.$store.state.quickAdd = 'agenda';
        },
        async onSaved() {
            this.showGoal = false;
            this.checkInGoal = null;
            await this.load();
        }
    }
});
</script>
